import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { useQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import type { z } from "zod";
import useBackendQueryEnabled from "@hooks/useBackendQueryEnabled";
import {
  profileUserStatsWireSchema,
  publicProfileUserStatsWireSchema,
} from "@libs/wire";
import { NoAuthError } from "@utils/NoAuthError";
import { RouterOutputs, trpc } from "@utils/trpc";

/**
 * Level configuration from the scoring config.
 */
export type LevelConfig = {
  minScore: number;
  level: number;
  name: string;
  vibe: string;
  shadowColor: string;
};

/**
 * Type for the user stats returned from the Postgres profile stats endpoint.
 */
type PrivateUserStats = RouterOutputs["user"]["stats"]["getForMe"];
type PublicUserStats = RouterOutputs["user"]["stats"]["getPublicProfile"];
type UserStats = PrivateUserStats | PublicUserStats;

type _ProfileStatsWireSchemaSubset =
  z.infer<typeof profileUserStatsWireSchema> extends PrivateUserStats
    ? true
    : never;
type _ProfileStatsWireSchemaSuperset =
  PrivateUserStats extends z.infer<typeof profileUserStatsWireSchema>
    ? true
    : never;
type _PublicProfileStatsWireSchemaSubset =
  z.infer<typeof publicProfileUserStatsWireSchema> extends PublicUserStats
    ? true
    : never;
type _PublicProfileStatsWireSchemaSuperset =
  PublicUserStats extends z.infer<typeof publicProfileUserStatsWireSchema>
    ? true
    : never;
const _profileStatsWireSchemaDriftCheck: [
  _ProfileStatsWireSchemaSubset,
  _ProfileStatsWireSchemaSuperset,
  _PublicProfileStatsWireSchemaSubset,
  _PublicProfileStatsWireSchemaSuperset,
] = [true, true, true, true];
void _profileStatsWireSchemaDriftCheck;

export const profileStatsQueryKey = (userId?: string) =>
  userId
    ? getQueryKey(trpc.user.stats.getPublicProfile, { userId }, "query")
    : getQueryKey(trpc.user.stats.getForMe);

const profileStatsQuery = (data: DataSession, userId?: string) =>
  data.cache.durableQueryOptions<UserStats>({
    queryKey: profileStatsQueryKey(userId),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("profileStatsQuery queryFn");
      }
      if (userId) {
        return data.api.user.stats.getPublicProfile.query({ userId });
      }
      return data.api.user.stats.getForMe.query();
    },
    schema: userId
      ? publicProfileUserStatsWireSchema
      : profileUserStatsWireSchema,
  });

/** Increment the cached fair plays count optimistically (callable outside React). */
export const incrementFairPlays = (data: DataSession) => {
  if (!data.isCurrent()) return;
  data.cache.setQueryDataDurable<UserStats>(
    data.queryClient,
    profileStatsQueryKey(),
    (cached) =>
      cached
        ? {
            ...cached,
            totalStreams: (cached.totalStreams ?? 0) + 1,
            thisMonth: cached.thisMonth
              ? {
                  ...cached.thisMonth,
                  totalStreams: (cached.thisMonth.totalStreams ?? 0) + 1,
                }
              : cached.thisMonth,
          }
        : cached,
  );
};

/**
 * Hook to read a user's profile stats from the Postgres-backed endpoint.
 * When no `userId` is provided, reads the authenticated user's stats
 * through the durable React Query cache.
 *
 * When `userId` is provided, fetches that user's stats under a separate key.
 */
export const useUserStats = (userId?: string) => {
  const data = useData();
  const enabledFromAuth = useBackendQueryEnabled();
  const statsQuery = useQuery({
    ...profileStatsQuery(data, userId),
    enabled: enabledFromAuth,
  });

  const effectiveStats = statsQuery.data ?? null;

  // Extract scoringLevels from stats for easier access
  const scoringLevels = effectiveStats?.scoringLevels ?? null;

  // Extract thisMonth stats if available
  const thisMonth = effectiveStats?.thisMonth ?? null;

  return {
    /** This Year stats (rolling 12 months) - top-level fields */
    stats: effectiveStats ?? null,
    /** This Month stats (current calendar month) */
    thisMonth,
    /** Only true on first load when no cached data exists (waits for hydration) */
    isLoadingStats: userId
      ? statsQuery.isLoading
      : statsQuery.isLoading && effectiveStats === null,
    /** True when refetching but we have cached data to show */
    isRefreshing: statsQuery.isFetching && effectiveStats !== null,
    /** Scoring config levels with thresholds and colors */
    scoringLevels,
  };
};
